'use client';

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { mergeClassNames } from '@/lib/utils';
import { focalPointToObjectPosition } from '@/lib/entity-image';

function computeCenteredPosition(
  focalX: number,
  focalY: number,
  naturalWidth: number,
  naturalHeight: number,
  containerWidth: number,
  containerHeight: number
): string {
  const imageAspect = naturalWidth / naturalHeight;
  const containerAspect = containerWidth / containerHeight;

  let posX: number;
  let posY: number;

  if (imageAspect > containerAspect) {
    // Image wider than container → horizontal crop
    const scale = containerHeight / naturalHeight;
    const scaledWidth = naturalWidth * scale;
    const excess = scaledWidth - containerWidth;
    if (excess > 0) {
      const idealOffset = scaledWidth * focalX - containerWidth / 2;
      posX = Math.max(0, Math.min(100, (idealOffset / excess) * 100));
    } else {
      posX = 50;
    }
    posY = 50;
  } else {
    // Image taller than container → vertical crop
    const scale = containerWidth / naturalWidth;
    const scaledHeight = naturalHeight * scale;
    const excess = scaledHeight - containerHeight;
    if (excess > 0) {
      const idealOffset = scaledHeight * focalY - containerHeight / 2;
      posY = Math.max(0, Math.min(100, (idealOffset / excess) * 100));
    } else {
      posY = 50;
    }
    posX = 50;
  }

  return `${Math.round(posX)}% ${Math.round(posY)}%`;
}

interface FocalPointImageProps {
  src: string;
  alt?: string;
  focalPointX: number;
  focalPointY: number;
  className?: string;
}

export function FocalPointImage({
  src,
  alt = '',
  focalPointX,
  focalPointY,
  className,
}: FocalPointImageProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [objectPosition, setObjectPosition] = useState(() =>
    focalPointToObjectPosition(focalPointX, focalPointY)
  );

  const recalculate = useCallback(() => {
    const img = imgRef.current;
    const container = img?.parentElement;
    if (!img || !container) return;

    const { naturalWidth, naturalHeight } = img;
    const { clientWidth, clientHeight } = container;
    if (!naturalWidth || !naturalHeight || !clientWidth || !clientHeight) return;

    setObjectPosition(
      computeCenteredPosition(
        focalPointX,
        focalPointY,
        naturalWidth,
        naturalHeight,
        clientWidth,
        clientHeight
      )
    );
  }, [focalPointX, focalPointY]);

  useEffect(() => {
    const img = imgRef.current;
    const container = img?.parentElement;
    if (!img || !container) return;

    const handleLoad = () => recalculate();
    img.addEventListener('load', handleLoad);

    if (img.complete && img.naturalWidth > 0) {
      recalculate();
    }

    const ro = new ResizeObserver(recalculate);
    ro.observe(container);

    return () => {
      img.removeEventListener('load', handleLoad);
      ro.disconnect();
    };
  }, [recalculate]);

  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      className={mergeClassNames('h-full w-full object-cover', className)}
      style={{ objectPosition }}
    />
  );
}
